/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {ColDef, GridOptions} from "@ag-grid-community/core";
// eslint-disable-next-line max-len
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {ConfigSectionWithAgTableComponent} from "@app/models/components/config-section-with-ag-table.component";
import {RegistryContexts} from "@app/models/constants/registry-constants";
import {QUERY_PARAM_OPEN_THERAPY_DETAILS} from "@app/models/constants/routing-constants";
import {CommonState} from "@app/store/common/common.state";
import {CustomPreferencesTreatmentPlan} from "@doctor-panel-modules/patient-treatment-plan/models/treatment-plan-constants";
import {AlertController, ModalController} from "@ionic/angular";
import {AssistantDataService} from "@modules/base/components/voice-assistant/voice-assistant-data.service";
import {CustomPreferencesService} from "@modules/base/services/custom-preferences.service";
import {PatientTreatmentPlanBaseState} from "@modules/doctor-panel/modules/patient-treatment-plan/store/patient-treatment-plan-base.state";
import {hasActionsExecuting} from "@ngxs-labs/actions-executing";
import {Select, Store} from "@ngxs/store";
import {
  ClinicalDataType,
  TherapyFrequencyLabelPipe,
  TrecTherapy,
  TrecTherapyDiary,
} from "@trec/api/clinical-data";
import {RangeDatesUnix} from "@trec/api/common";
import {C3SnackbarStatusService} from "@trec/components/snackbar-status";
import {StoreAction} from "@trec/components/store";
import {
  C3TextCellComponent,
  C3TmplCellComponent,
  C3TmplCellOptions,
} from "@trec/components/table";
import {addMonths, format, max, subMonths} from "date-fns";
import {it as localeIt} from "date-fns/locale";
import {Observable, Subscription, of} from "rxjs";
import {filter, take} from "rxjs/operators";
import {TpBaseActions} from "../../../store/patient-treatment-plan-base.actions";
import {
  EditTherapyDialogComponent,
  EditTherapyDialogData,
} from "../../edit/edit-therapy-dialog/edit-therapy-dialog.component";

@Component({
  selector: "c3-config-therapies",
  templateUrl: "./config-therapies.component.html",
  styleUrls: ["./config-therapies.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ConfigTherapiesComponent
  extends ConfigSectionWithAgTableComponent<TrecTherapy>
  implements OnInit, AfterViewInit, OnDestroy {
  // Data source //////////////////////////////////////////////////////////////////////////////////

  @Select(PatientTreatmentPlanBaseState.therapies) therapies$: Observable<TrecTherapy[]>;

  @Select(
    hasActionsExecuting([
      TpBaseActions.FetchTherapies,
      TpBaseActions.InterruptTherapy,
      TpBaseActions.SuspendTherapy,
      TpBaseActions.ResumeTherapy,
    ])
  )
  loading$: Observable<boolean>;

  // View Components //////////////////////////////////////////////////////////////////////////////

  @ViewChild("insulinCellTmpl")
  insulinCellTmpl!: TemplateRef<unknown>;

  @ViewChild("assumptionsCellTmpl")
  assumptionsCellTmpl!: TemplateRef<unknown>;

  @ViewChild("actionsCellTmpl")
  actionsCellTmpl!: TemplateRef<unknown>;

  // AgGrid fields ////////////////////////////////////////////////////////////////////////////////

  readonly customAgGridOptions: GridOptions = {
    onRowClicked: (event) => this.openEditDialog({therapy: event.data}), // Open therapy details

    domLayout: "autoHeight",
    pagination: true,
    paginationAutoPageSize: false,
    paginationPageSize: 10,

    // Show/hide ended therapies
    isExternalFilterPresent: () => !this.showEnded,

    // Show/hide ended therapies
    doesExternalFilterPass: (node) => this.showEnded || !(node.data as TrecTherapy).isTerminated,

    rowClassRules: {
      // Mark with grey rows of therapies that are terminated
      "app-table-row-color-grey-light": (params) => (params.data as TrecTherapy).isTerminated,
    },
  };

  columnDefs: ColDef[];

  protected readonly PREF_KEY_TABLE_SORT = CustomPreferencesTreatmentPlan.THERAPIES_LIST_SORT;
  protected readonly PREF_KEY_HIDDEN_COLUMNS =
    CustomPreferencesTreatmentPlan.THERAPIES_LIST_HIDDEN_COLS;

  // I/O //////////////////////////////////////////////////////////////////////////////////////////

  private _context: RegistryContexts[];
  @Input()
  public get context(): RegistryContexts[] {
    return this._context;
  }
  public set context(value: RegistryContexts[]) {
    this._context = value;

    this.isDiabetesContext = false;
    this.context.forEach((element) => {
      this.isDiabetesContext = this.DIABETES_CTX.includes(element) ? true : false;
    });

    this.updateVisibleColumns();
  }

  isDiabetesContext: boolean;

  /**
   * If this query param is contained in route with value "true", open automatically the "insert new therapy" dialog.
   */
  readonly QUERY_ADD_THERAPY = "openInsertTherapy";

  readonly CTX_ONCOLOGY = RegistryContexts.ONCOLOGY;
  readonly CTX_ONCOLOGY_BREAST_CANCER = RegistryContexts.ONCOLOGY_BREAST_CANCER;
  readonly PREGNANCY = RegistryContexts.PREGNANCY;

  readonly today = new Date().valueOf();

  readonly ClinicalDataType = ClinicalDataType;

  get touched$(): Observable<boolean> {
    return of(true);
  }

  get valid$(): Observable<boolean> {
    return of(true);
  }

  private readonly DIABETES_CTX = [
    RegistryContexts.DIABETES1,
    RegistryContexts.DIABETES2,
    RegistryContexts.GESTATIONAL,
    RegistryContexts.PREPREGNANCY,
  ];

  /**
   * Show ended therapies.
   */
  showEnded = false;

  // WithTableComponent implementation //////////

  // sortOptions$: Observable<Sort>;
  sortPreferenceKey = CustomPreferencesTreatmentPlan.THERAPIES_LIST_SORT;

  hiddenColsOptions$: Observable<string[]>;
  hiddenColsPreferenceKey = CustomPreferencesTreatmentPlan.THERAPIES_LIST_HIDDEN_COLS;

  // Chart vars ///////////////////////////////////////////////////////////////////////////////////

  readonly viewPeriod: RangeDatesUnix = {
    start: subMonths(new Date(), 3).valueOf(),
    end: addMonths(new Date(), 3).valueOf(),
  };

  diaryTherapies: Map<string, TrecTherapyDiary[]> = new Map();

  // Others ///////////////////////////////////////////////////////////////////////////////////////

  viewType: "table" | "chart" = "table";

  touched: boolean;
  valid: boolean;

  private readonly subscr = new Subscription();

  constructor(
    private store: Store,
    protected router: Router,
    protected route: ActivatedRoute,
    private modalCtrl: ModalController,
    private c3SnackbarStatus: C3SnackbarStatusService,
    protected cd: ChangeDetectorRef,
    protected preferences: CustomPreferencesService,
    private alertCtrl: AlertController,
    private therapyFrequencyLabelPipe: TherapyFrequencyLabelPipe,
    private assistantDataService: AssistantDataService
  ) {
    super(preferences, router, route);
  }

  getCurrentDate() {
    return new Date().valueOf()
  }

  ngOnInit() {
    super.ngOnInit();
    this.store.dispatch(new TpBaseActions.FetchTherapies());

    if (this.route.snapshot.queryParamMap.get(this.QUERY_ADD_THERAPY) === "true") {
      // Open therapy insert dialog
      this.openEditDialog({});
    }

    const openTherapyId = this.route.snapshot.queryParamMap.get(QUERY_PARAM_OPEN_THERAPY_DETAILS);
    if (openTherapyId) {
      // Wait for therapy list loading and then open therapy details modal
      this.subscr.add(
        this.therapies$
          .pipe(
            filter((list) => list?.length > 0),
            take(1)
          )
          .subscribe((list) => {
            const therapy = list.find((t) => t.id === openTherapyId);
            if (therapy) {
              this.openEditDialog({therapy});
            }
          })
      );
    }

    this.assistantDataService.drugResponse$.subscribe((data) => {
      console.log('[ConfigTherapiesComponent] Ricevuta risposta DRUG:', data);
      this.handleDrugResponse(data);
    });
  }

  ngAfterViewInit() {
    // Init table
    this.columnDefs = [
      {
        headerName: `Farmaco`,
        colId: "pharma",
        field: "data.drug.name",
        flex: 25,
        minWidth: 200,
        cellRendererSelector: () => ({component: C3TextCellComponent}),
        // Custom comparator is needed because agGrid sort items using case-sensitive algorithm
        // https://stackoverflow.com/questions/56273069/how-to-make-sorting-key-insensitive-in-ag-grid
        comparator: (valueA: string, valueB: string) =>
          valueA?.toLowerCase().localeCompare(valueB?.toLowerCase()),
      },
      // {
      //   headerName: `Farmaco Insulinico`,
      //   headerClass: "app-table-header-cell-text-wrap",
      //   colId: "insulin",
      //   flex: 15,
      //   minWidth: 96,
      //   cellRenderer: C3TmplCellComponent,
      //   cellRendererParams: { template: this.insulinCellTmpl } as C3TmplCellOptions,
      //   comparator: (_valueA: undefined, _valueB: undefined, nodeA: RowNode, nodeB: RowNode) => {
      //     const aVal = (nodeA.data as TrecTherapy).data.drug.active_ingredient
      //       .toLowerCase()
      //       .includes("insulin")
      //       ? 1
      //       : 0;
      //     const bVal = (nodeB.data as TrecTherapy).data.drug.active_ingredient
      //       .toLowerCase()
      //       .includes("insulin")
      //       ? 1
      //       : 0;

      //     return aVal > bVal ? 1 : -1;
      //   },
      // },
      {
        headerName: `Termine`,
        colId: "metadata.end_date",
        valueGetter: (params) => {
          const therapy = params.data as TrecTherapy;
          return !therapy.hasEnding
            ? "Nessuno"
            : format(therapy.metadata.end_date, "dd/MM/yyyy", {locale: localeIt});
        },
        flex: 10,
        minWidth: 120,
        cellRendererSelector: () => ({component: C3TextCellComponent}),
      },
      {
        headerName: `Assunzioni`,
        colId: "assumptions",
        flex: 28,
        minWidth: 200,
        autoHeight: true,
        cellRendererSelector: () => ({component: C3TmplCellComponent}),
        cellRendererParams: {template: this.assumptionsCellTmpl} as C3TmplCellOptions,
      },
      {
        headerName: `Frequenza`,
        colId: "frequency",
        valueGetter: (params) => {
          const therapy = params.data as TrecTherapy;

          if (therapy.data.variable.assumptions.length > 0) {
            // Theraphy with variable assumption frequency
            return "Posologia variabile";
          } else {
            // Theraphy with regular assumption frequency
            return this.therapyFrequencyLabelPipe.transform(therapy);
          }
        },
        flex: 10,
        minWidth: 200,
        cellRendererSelector: () => ({component: C3TextCellComponent}),
      },
      {
        headerName: `Prescritto da`,
        colId: "doctor",
        valueGetter: (params) => {
          const therapy = params.data as TrecTherapy;

          if (therapy.metadata.doctor) {
            return therapy.metadata.doctor.name;
          } else {
            return "-"
          }
        },
        flex: 10,
        minWidth: 200,
        cellRendererSelector: () => ({component: C3TextCellComponent}),
      },
      {
        headerName: `Azioni`,
        colId: "actions",
        flex: 12,
        minWidth: 200,
        cellRendererSelector: () => ({component: C3TmplCellComponent}),
        cellRendererParams: {template: this.actionsCellTmpl} as C3TmplCellOptions,
      },
    ];

    this.updateVisibleColumns();

    this.subscr.add(
      this.therapies$.subscribe(() => {
        this.diaryTherapies = this.createFakeTherapies();
        this.cd.markForCheck();
      })
    );
  }

  canEditOrDelete(doctorId?: string) {
    if (!doctorId) {
      return false;
    }
    const id = this.store.selectSnapshot(CommonState.userId);

    return id === doctorId;
  }

  private updateVisibleColumns() {
    // Hide "is insulin therapy" column if not in a diabetic context
    //this.agColumnApi?.setColumnVisible("insulin", this.isDiabetesContext);
  }

  // View and Edit ////////////////////////////////////////////////////////////////////////////////

  async openEditDialog(
    options: {
      therapy?: TrecTherapy | null;
      insulinTherapy?: boolean;
      oncoTherapy?: boolean;
    },
    event?: Event
  ) {
    event?.stopImmediatePropagation(); // Prevent default action when row is clicked

    const canEditOrDelete = this.canEditOrDelete(options.therapy?.metadata.doctor?.id);

    const modal = await this.modalCtrl.create({
      component: EditTherapyDialogComponent,
      componentProps: {
        data: {
          therapy: options.therapy ?? null,
          insulinTherapy: options.insulinTherapy ?? false,
          oncoTherapy: options.oncoTherapy ?? false,
          context: this.context,
          insertDefaults: {atLeastOneAssumptionNeeded: true},
          canEditOrDelete
        } as EditTherapyDialogData,
      },
      cssClass: "c3-modal-width-lg c3-modal-height-lg",
    });
    await modal.present();

    // Save in route that insert dialog is opened
    if (!options.therapy && !options.insulinTherapy && !options.oncoTherapy) {
      this.router.navigate([], {
        queryParams: {[this.QUERY_ADD_THERAPY]: "true"},
        queryParamsHandling: "merge",
      });
    }

    const res = await modal.onDidDismiss();

    // Remove from route, query param
    this.router.navigate([], {
      queryParams: {[this.QUERY_ADD_THERAPY]: "false"},
      queryParamsHandling: "merge",
    });

    // User requested for the special insert therapy form
    if (res.data?.openInsertOncoTherapy) {
      this.openEditDialog({oncoTherapy: true});
    }
  }

  async openEnableInterruptDialog(
    therapy: TrecTherapy,
    action: "suspend" | "resume" | "interrupt",
    event?: Event
  ) {
    event?.stopImmediatePropagation(); // Prevent default action when row is clicked

    const {header, message} = this.prepareEditTherapyTimeAlertContent(action);

    const alert = await this.alertCtrl.create({
      header,
      message,
      buttons: [
        {
          text: "No",
          role: "cancel",
        },
        {
          text: "Sì",
          handler: () => {
            const now = new Date().valueOf();
            const structure = this.store.selectSnapshot(CommonState.currentStructureId)!;
            const doctorId = this.store.selectSnapshot(CommonState.userId)!;
            const doctorName =
              this.store.selectSnapshot(CommonState.loggedUserData)?.personalData?.surname! +
              " " +
              this.store.selectSnapshot(CommonState.loggedUserData)?.personalData?.name!;

            switch (action) {
              case "suspend":
                // Date of suspension is the greatest between now and the start date of the therapy... why? Because the therapy taken into
                // account could start in the future. In this case, if we set the suspension date to now, the backend update the therapy
                // start date setting it to now (which is a date that comes before the effective start date decided by the doctor). This
                // behavior would break the chronological order of start and end dates of the therapies data saved in the history array.
                const suspendDate = max([now, therapy.metadata.start_date]).valueOf();
                this.store
                  .dispatch(
                    new TpBaseActions.SuspendTherapy({
                      therapy,
                      date: suspendDate,
                      structure: structure,
                      doctorId: doctorId,
                      doctorName: doctorName,
                    })
                  )
                  .subscribe(() => {
                    this.c3SnackbarStatus.showSuccess({message: "Terapia sospesa correttamente"});
                  });
                break;

              case "resume":
                this.store
                  .dispatch(
                    new TpBaseActions.ResumeTherapy({
                      therapy,
                      date: now,
                      structure: structure,
                      doctorId: doctorId,
                      doctorName: doctorName,
                    })
                  )
                  .subscribe(() => {
                    this.c3SnackbarStatus.showSuccess({message: "Terapia ripresa correttamente"});
                  });
                break;

              case "interrupt":
                this.store
                  .dispatch(
                    new TpBaseActions.InterruptTherapy({
                      therapy,
                      date: now,
                      structure: structure,
                      doctorId: doctorId,
                      doctorName: doctorName,
                    })
                  )
                  .subscribe(() => {
                    this.c3SnackbarStatus.showSuccess({
                      message: "Terapia interrotta correttamente",
                    });
                  });
                break;

              default:
                break;
            }
          },
        },
      ],
    });

    await alert.present();
  }

  toggleShowEnded(checked: boolean) {
    this.showEnded = checked;

    this.diaryTherapies = this.createFakeTherapies();
    this.cd.markForCheck();

    this.agGridApi?.onFilterChanged();
  }

  onRefreshList() {
    this.store.dispatch(new TpBaseActions.FetchTherapies());
  }

  prepareSaveDataAction(): StoreAction | null {
    return null;
  }

  resetTouched(): void { }

  private createFakeTherapies(): Map<string, TrecTherapyDiary[]> {
    const newMap: Map<string, TrecTherapyDiary[]> = new Map();

    const therapies = this.store.selectSnapshot(PatientTreatmentPlanBaseState.therapies);
    const filteredtherapies = therapies.filter((t) => this.showEnded || !t.isTerminated);

    // Create fake diary
    filteredtherapies.forEach((therapy) => {
      newMap.set(therapy.id || "", [
        new TrecTherapyDiary({assumptions: [], currentTherapy: therapy}),
      ]);
    });

    return newMap;
  }

  private prepareEditTherapyTimeAlertContent(action: "suspend" | "resume" | "interrupt"): {
    header: string;
    message: string;
  } {
    switch (action) {
      case "suspend":
        return {
          header: "Sospensione terapia",
          message: "Sei veramente sicura/o di voler sospendere questa terapia?",
        };

      case "resume":
        return {
          header: "Ripresa terapia",
          message: "Sei veramente sicura/o di voler riprendere questa terapia?",
        };

      case "interrupt":
        return {
          header: "Interruzione terapia",
          message: "Sei veramente sicura/o di voler interrompere questa terapia?",
        };

      default:
        return {
          header: "Azione sconosciuta",
          message: "",
        };
    }
  }

  //ASSISTENTE VOCALE PRESCRIZIONI TERAPIA PER PAZIENTE X
  handleDrugResponse(data: any) {
    // Logica per gestire la risposta
  }

  ngOnDestroy(): void {
    this.subscr.unsubscribe();
  }
}
